import React from 'react';
import { ArrayHelpers, StringHelpers, SystemText } from 'e4shared';
import { useSelector } from 'react-redux';
import Dropdown from 'components/general/dropdown';
import { apiGet } from 'services';

export const DefaultTargetValues = {
    value: 100,
    range: 'T',
    period: 'all',
    duration: 1,
};

export const MetricTarget = ({ value, error, mandatory, metric, onChange }) => {
    const [metricPrefix, setMetricPrefix] = React.useState('£');
    const [goals, setGoals] = React.useState([]);
    const [thisValue, setThisValue] = React.useState({ ...DefaultTargetValues, ...value });

    const user = useSelector(state => state.user.user);
    const showFormHelp = useSelector(state => state.ui.showFormHelp);

    React.useEffect(() => {
        apiGet('quotas', user.guid, response => setGoals(response));
    }, []);

    React.useEffect(() => {
        setThisValue(value);
    }, [value]);

    React.useEffect(() => {
        if (metric?.format === 'M') {
            setMetricPrefix(user.currencySymbol);
        } else {
            setMetricPrefix('');
        }
    }, [metric]);

    const changeValue = (prop, value) => {
        const temp = { ...thisValue };
        temp[prop] = value;
        setThisValue(temp);
        onChange(temp);
    };

    const getAvailableRanges = () => {
        const ranges = [
            { value: 'T', onClick: () => changeValue('range', 'T') },
            { value: 'P', onClick: () => changeValue('range', 'P') },
        ];

        ranges.forEach(r => (r.name = SystemText(`Objective/Manage/Fields/MetricTarget/Ranges/${r.value}`)));

        const availableGoals = getAvailableGoals();
        return ArrayHelpers.existsAndHasLength(availableGoals) ? ranges : [ranges[0]];
    };

    const getAvailablePeriods = () => {
        const periods = [
            { value: 'all', onClick: () => changeValue('period', 'all') },
            { value: 'week', onClick: () => changeValue('period', 'week') },
            { value: 'month', onClick: () => changeValue('period', 'month') },
            { value: 'quarter', onClick: () => changeValue('period', 'quarter') },
        ];

        periods.forEach(
            p => (p.name = SystemText(`Objective/Manage/Fields/MetricTarget/Periods/${StringHelpers.capitalise(p.value)}`))
        );

        return periods;
    };

    const getAvailableGoals = () => {
        if (metric?.id && ArrayHelpers.existsAndHasLength(goals)) {
            const avail = goals
                .filter(
                    g =>
                        g.metric_Id === metric.id &&
                        ((metric.integration === 'CRM' && g.crm) || (metric.integration === g.integration && !g.crm))
                )
                .map(g => ({ ...g, value: g.id, onClick: () => changeValue('goal', g.id) }));
            return avail;
        } else {
            return [];
        }
    };

    const availableRanges = getAvailableRanges();
    const availablePeriods = getAvailablePeriods();
    const availableGoals = getAvailableGoals();

    return (
        <div className={`form-group ${error ? 'has-error' : ''}`}>
            <label>
                {SystemText('Objective/Manage/Fields/MetricTarget/Label')}
                {mandatory && <span className="mandatory">*</span>}
            </label>
            {showFormHelp && (
                <span className="form-control-help">{SystemText('Objective/Manage/Fields/MetricTarget/Help')}</span>
            )}
            <div className="inline-picker">
                {SystemText('Objective/Manage/Fields/MetricTarget/Text/Must')} {thisValue.range === 'T' ? metricPrefix : ''}
                <input
                    type="number"
                    className="ml3 form-control-sm inline"
                    style={{ width: thisValue.range === 'P' ? '50px' : '100px' }}
                    value={thisValue.value}
                    onChange={e => changeValue('value', isNaN(e.target.value) ? 0 : parseInt(e.target.value, 10))}
                />{' '}
                <Dropdown
                    label={
                        availableRanges.find(x => x.value === thisValue.range)?.name ||
                        SystemText('Objective/Manage/Fields/MetricTarget/Select')
                    }
                    small
                    id="availableRanges"
                    options={availableRanges}
                />{' '}
                {thisValue.range === 'P' ? (
                    <Dropdown
                        label={
                            availableGoals.find(x => x.value === thisValue.goal)?.name ||
                            SystemText('Objective/Manage/Fields/MetricGoal/Select')
                        }
                        small
                        id="availableGoals"
                        options={availableGoals}
                    />
                ) : (
                    <Dropdown
                        label={
                            availablePeriods.find(x => x.value === thisValue.period)?.name ||
                            SystemText('Objective/Manage/Fields/MetricTarget/Select')
                        }
                        small
                        id="availablePeriods"
                        options={availablePeriods}
                    />
                )}
                {thisValue.period !== 'all' && (
                    <span>
                        {' '}
                        {SystemText('Objective/Manage/Fields/MetricTarget/Text/ForAtLeast')}{' '}
                        <input
                            type="number"
                            className="ml3 form-control-sm inline"
                            style={{ width: '50px' }}
                            value={thisValue.duration || 1}
                            onChange={e => changeValue('duration', isNaN(e.target.value) ? 0 : parseInt(e.target.value, 10))}
                        />{' '}
                        {SystemText(
                            'Objective/Manage/Fields/MetricTarget/Text/ForPeriod',
                            thisValue.period
                                ? StringHelpers.pluralise(thisValue.period, 2)
                                : SystemText('Objective/Manage/Fields/MetricTarget/Periods/Periods')
                        )}
                    </span>
                )}
            </div>
            {error && <span className="help-block">{error}</span>}
        </div>
    );
};

export default MetricTarget;
